import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Text, Button, Loader } from '@pingux/astro';
import ContentCopyIcon from '@pingux/mdi-react/ContentCopyIcon';
import CheckIcon from '@pingux/mdi-react/CheckIcon';
import { credentialsService } from '@services/credentials';
import { SharedCredential, SharedCredentialMetadata } from '@app-types/tap-partner-vault';

const SharedCredentialPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [metadata, setMetadata] = useState<SharedCredentialMetadata | null>(null);
  const [credential, setCredential] = useState<SharedCredential | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [secretRevealed, setSecretRevealed] = useState(false);
  const [unlockedAccessCode, setUnlockedAccessCode] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  const [totpRefreshError, setTotpRefreshError] = useState<string | null>(null);
  const [totpRefreshing, setTotpRefreshing] = useState(false);

  // Access code state
  const [accessCode, setAccessCode] = useState(['', '', '', '']);
  const [unlocking, setUnlocking] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (!token) return;
    credentialsService.getSharedMetadata(token)
      .then(setMetadata)
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (!credential?.totp) return;

    const intervalId = window.setInterval(() => setNow(Date.now()), 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [credential?.totp]);

  useEffect(() => {
    if (!credential?.totp || !token || !unlockedAccessCode) return;

    const expiresAt = new Date(credential.totp.generated_at).getTime() +
      credential.totp.expires_in_seconds * 1000;
    const refreshDelay = Math.max(0, expiresAt - Date.now() + 250);
    const timeoutId = window.setTimeout(async () => {
      try {
        setTotpRefreshing(true);
        setTotpRefreshError(null);
        const result = await credentialsService.unlockShared(token, unlockedAccessCode);
        setCredential(result);
        setSecretRevealed(true);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Failed to refresh OTP';
        setTotpRefreshError(msg);
      } finally {
        setTotpRefreshing(false);
      }
    }, refreshDelay);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [credential?.totp?.expires_in_seconds, credential?.totp?.generated_at, token, unlockedAccessCode]);

  const handleDigitChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...accessCode];
    next[index] = digit;
    setAccessCode(next);
    setCodeError(null);
    if (digit && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleDigitKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !accessCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleUnlock = async () => {
    const code = accessCode.join('');
    if (code.length < 4) {
      setCodeError('Please enter the full 4-digit access code.');
      return;
    }
    if (!token) return;
    try {
      setUnlocking(true);
      setCodeError(null);
      const result = await credentialsService.unlockShared(token, code);
      setCredential(result);
      setUnlockedAccessCode(code);
      setTotpRefreshError(null);
      setSecretRevealed(true);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Invalid access code';
      setCodeError(msg);
      setAccessCode(['', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setUnlocking(false);
    }
  };

  const handleCopy = async (value: string, field: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      // clipboard unavailable
    }
  };

  const formatExpiry = (isoString: string) => {
    const expires = new Date(isoString);
    const now = new Date();
    const diffMs = expires.getTime() - now.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    if (diffHours > 0) return `Expires in ${diffHours}h ${diffMins}m`;
    if (diffMins > 0) return `Expires in ${diffMins}m`;
    return 'Expiring soon';
  };

  const secretLabel = (type: string) => {
    if (type === 'oauth_client') return 'Client Secret';
    if (type === 'admin_account') return 'Password';
    if (type === 'basic_auth') return 'Password';
    return 'API Key';
  };

  const accountLabel = (type: string) => {
    if (type === 'oauth_client') return 'Client ID';
    if (type === 'admin_account') return 'Username';
    if (type === 'basic_auth') return 'Username';
    return 'Account';
  };

  const CopyButton: React.FC<{ value: string; field: string }> = ({ value, field }) => (
    <Button
      variant="link"
      onPress={() => handleCopy(value, field)}
      sx={{ color: copiedField === field ? '#059669' : '#6b7280', padding: '0', minWidth: 0 }}
    >
      {copiedField === field ? <CheckIcon size={15} /> : <ContentCopyIcon size={15} />}
    </Button>
  );

  const refreshTotp = async () => {
    if (!token || !unlockedAccessCode) return;

    try {
      setTotpRefreshing(true);
      setTotpRefreshError(null);
      const result = await credentialsService.unlockShared(token, unlockedAccessCode);
      setCredential(result);
      setSecretRevealed(true);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to refresh OTP';
      setTotpRefreshError(msg);
    } finally {
      setTotpRefreshing(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Loader />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Box sx={{ textAlign: 'center', maxWidth: '400px' }}>
          <Text sx={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>🔒</Text>
          <Text as="h2" sx={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827', marginBottom: '0.5rem' }}>
            Link Unavailable
          </Text>
          <Text sx={{ color: '#6b7280' }}>{error}</Text>
        </Box>
      </Box>
    );
  }

  if (!metadata) return null;

  const labelStyle = {
    fontSize: '0.7rem',
    fontWeight: '500',
    color: '#6b7280',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    marginBottom: '0.25rem',
  };

  const rowStyle = {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: '0.75rem 0',
    borderBottom: '1px solid #f3f4f6',
    gap: '1rem',
  };
  const totpSecondsRemaining = credential?.totp
    ? Math.max(
        0,
        Math.ceil(
          (
            new Date(credential.totp.generated_at).getTime() +
            credential.totp.expires_in_seconds * 1000 -
            now
          ) / 1000
        )
      )
    : null;

  const header = (
    <Box sx={{ textAlign: 'center', marginBottom: '1.5rem' }}>
      <Text as="h1" sx={{ fontSize: '1.25rem', fontWeight: '700', color: '#111827' }}>
        {metadata.partner} — {metadata.tenant_id}
      </Text>
      <Text sx={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.25rem' }}>
        {metadata.credential_type.replace(/_/g, ' ')} · {metadata.environment}
      </Text>
      <Box sx={{
        display: 'inline-block',
        marginTop: '0.5rem',
        padding: '0.2rem 0.75rem',
        backgroundColor: '#fef3c7',
        borderRadius: '9999px',
      }}>
        <Text sx={{ fontSize: '0.75rem', color: '#92400e', fontWeight: '500' }}>
          {formatExpiry(metadata.expires_at)}
        </Text>
      </Box>
    </Box>
  );

  // Access code gate
  if (!credential) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', padding: '3rem 1rem' }}>
        <Box sx={{ width: '100%', maxWidth: '400px' }}>
          {header}

          <Box sx={{
            backgroundColor: 'white',
            borderRadius: '0.75rem',
            boxShadow: '0 1px 3px 0 rgba(0,0,0,0.1)',
            padding: '1.75rem 1.5rem',
            textAlign: 'center',
          }}>
            <Text sx={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>🔑</Text>
            <Text as="h2" sx={{ fontSize: '1rem', fontWeight: '600', color: '#111827', marginBottom: '0.25rem' }}>
              Enter Access Code
            </Text>
            <Text sx={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '1.5rem' }}>
              Enter the 4-digit code provided by the person who shared this credential.
            </Text>

            <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
              {accessCode.map((digit, i) => (
                <input
                  key={i}
                  ref={el => { inputRefs.current[i] = el; }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={e => handleDigitChange(i, e.target.value)}
                  onKeyDown={e => handleDigitKeyDown(i, e)}
                  style={{
                    width: '3rem',
                    height: '3.5rem',
                    textAlign: 'center',
                    fontSize: '1.5rem',
                    fontWeight: '700',
                    fontFamily: 'monospace',
                    border: `2px solid ${codeError ? '#ef4444' : '#d1d5db'}`,
                    borderRadius: '0.5rem',
                    outline: 'none',
                    color: '#111827',
                  }}
                />
              ))}
            </div>

            {codeError && (
              <Text sx={{ fontSize: '0.8rem', color: '#dc2626', marginBottom: '0.75rem' }}>
                {codeError}
              </Text>
            )}

            <Button
              variant="primary"
              onPress={handleUnlock}
              isLoading={unlocking}
              sx={{ width: '100%', justifyContent: 'center' }}
            >
              Unlock Credential
            </Button>
          </Box>

          <Text sx={{ fontSize: '0.75rem', color: '#9ca3af', textAlign: 'center', marginTop: '1rem' }}>
            This link was shared via TAP Partner Vault and expires {new Date(metadata.expires_at).toLocaleString()}.
          </Text>
        </Box>
      </Box>
    );
  }

  // Unlocked credential view
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', padding: '3rem 1rem' }}>
      <Box sx={{ width: '100%', maxWidth: '540px' }}>
        {header}

        <Box sx={{
          backgroundColor: 'white',
          borderRadius: '0.75rem',
          boxShadow: '0 1px 3px 0 rgba(0,0,0,0.1)',
          padding: '0 1.5rem',
        }}>
          {credential.url && (
            <div style={rowStyle}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text sx={labelStyle}>URL</Text>
                <Text sx={{ fontSize: '0.875rem', wordBreak: 'break-all', color: '#2563eb' }}>
                  {credential.url}
                </Text>
              </div>
              <CopyButton value={credential.url} field="url" />
            </div>
          )}

          {credential.account_name && (
            <div style={rowStyle}>
              <div>
                <Text sx={labelStyle}>{accountLabel(credential.credential_type)}</Text>
                <Text sx={{ fontSize: '0.875rem', color: '#059669', fontWeight: '500' }}>
                  {credential.account_name}
                </Text>
              </div>
              <CopyButton value={credential.account_name} field="account" />
            </div>
          )}

          <div style={{ ...rowStyle, borderBottom: credential.totp ? '1px solid #f3f4f6' : 'none' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Text sx={labelStyle}>{secretLabel(credential.credential_type)}</Text>
              <Text sx={{
                fontSize: '0.875rem',
                fontFamily: 'monospace',
                wordBreak: 'break-all',
                color: secretRevealed ? '#111827' : '#9ca3af',
              }}>
                {secretRevealed ? credential.secret : '••••••••••••••••'}
              </Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
              {secretRevealed && <CopyButton value={credential.secret} field="secret" />}
              <Button
                variant="link"
                onPress={() => setSecretRevealed(prev => !prev)}
                sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap', color: '#6b7280' }}
              >
                {secretRevealed ? 'Hide' : 'Reveal'}
              </Button>
            </div>
          </div>

          {credential.totp && (
            <div style={{
              ...rowStyle,
              display: 'block',
              textAlign: 'center',
              borderBottom: 'none',
            }}>
              <Text sx={{ ...labelStyle, textAlign: 'center' }}>Synced OTP</Text>
              <div style={{
                margin: '0.5rem auto 0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.75rem',
                flexWrap: 'wrap',
              }}>
                <Text sx={{
                  fontSize: '2rem',
                  fontWeight: '700',
                  fontFamily: 'monospace',
                  letterSpacing: '0.2em',
                  color: '#111827',
                }}>
                  {credential.totp.code}
                </Text>
                <CopyButton value={credential.totp.code} field="totp" />
                <Button
                  variant="link"
                  onPress={refreshTotp}
                  isDisabled={totpRefreshing}
                  sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap', color: '#6b7280' }}
                >
                  {totpRefreshing ? 'Refreshing...' : 'Refresh'}
                </Button>
              </div>
              <Text sx={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
                {credential.totp.issuer} - {credential.totp.account_label}
                {totpSecondsRemaining !== null && ` - ${totpSecondsRemaining}s remaining`}
              </Text>
              {totpRefreshError && (
                <Text sx={{ fontSize: '0.75rem', color: '#dc2626', marginTop: '0.4rem' }}>
                  {totpRefreshError}
                </Text>
              )}
            </div>
          )}
        </Box>

        <Text sx={{ fontSize: '0.75rem', color: '#9ca3af', textAlign: 'center', marginTop: '1rem' }}>
          This link was shared via TAP Partner Vault and expires {new Date(credential.expires_at).toLocaleString()}.
        </Text>
      </Box>
    </Box>
  );
};

export default SharedCredentialPage;
